import { useState, useEffect, useCallback, useRef } from "react";
import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import { fetchWithRefresh } from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import SearchIcon from "/public/images/icons/search.svg?react";
import TextInput from "../../components/TextInput";
import DatePicker from "../../components/DatePicker";
import BookingCard from "../../components/BookingCard/Participant";
import Button from "../../components/Button";

const PAGE_SIZE = 25;

export default function ParticipantBookings() {
  const { setTitle, setBackButton, setFilterFunc } = useHeaderContext();
  const { user, logout, updateTokens } = useUserContext();
  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [filterTerm] = useState("");
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActivity, setSelectedActivity] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState([]);
  const [venueFilters, setVenueFilters] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const pageRef = useRef(0);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (pageNum) => {
    try {
      if (pageNum === 0) setLoading(true);
      else setLoadingMore(true);

      const url = `${import.meta.env.VITE_API_URL}bookings/bookings?userLoginId=${user?.id}&page=${pageNum}&pagesize=${PAGE_SIZE}&hidenotbooked=1`;
      const response = await fetchWithRefresh(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });

      if (!response.ok) {
        toast.error("Failed to load bookings.");
        setHasMore(false);
        return;
      }

      const data = await response.json();
      const incoming = Array.isArray(data) ? data : (data.bookings ?? data.data ?? data.$values ?? []);
      const totalCount = data.recordcount?.recordCount ?? incoming.length;

      setBookings((prev) => {
        const merged = pageNum === 0 ? incoming : [...prev, ...incoming];
        return [...merged].sort((a, b) => (a.dateSlot ?? "") > (b.dateSlot ?? "") ? -1 : 1);
      });

      setHasMore((pageNum + 1) * PAGE_SIZE < totalCount);
    } catch (error) {
      toast.error("Failed to load bookings.");
      setHasMore(false);
      console.error(error);
    } finally {
      if (pageNum === 0) setLoading(false);
      else setLoadingMore(false);
    }
  }, [user, logout, updateTokens]);

  const filterBookings = useCallback(() => {
    let filtered = bookings;

    if (filterTerm) {
      filtered = filtered.filter((booking) =>
        booking.orgPartnerFriendlyName?.toLowerCase().includes(filterTerm.toLowerCase())
      );
    }

    if (selectedVenue.length > 0) {
      filtered = filtered.filter((booking) =>
        selectedVenue.includes(booking.orgPartnerFriendlyName)
      );
    }

    if (dateFrom) {
      filtered = filtered.filter((booking) =>
        booking.dateSlot && booking.dateSlot.slice(0, 10) >= dateFrom
      );
    }

    if (dateTo) {
      filtered = filtered.filter((booking) =>
        booking.dateSlot && booking.dateSlot.slice(0, 10) <= dateTo
      );
    }

    if (selectedActivity.length > 0) {
      filtered = filtered.filter((booking) =>
        selectedActivity.includes(booking.baseActivityId)
      );
    }

    setFilteredBookings(filtered);
  }, [filterTerm, selectedVenue, dateFrom, dateTo, selectedActivity, bookings]);

  const searchBookings = useCallback((term) => {
    setSearchTerm(term);
    const lower = term.toLowerCase();
    if (term) {
      const matched = bookings.filter((booking) =>
        booking.orgPartnerFriendlyName?.toLowerCase().includes(lower) ||
        booking.baseActivityName?.toLowerCase().includes(lower) ||
        booking.participants?.some((p) => p.participantName?.toLowerCase().includes(lower))
      );
      setFilteredBookings(matched);
      setVenueFilters(
        Array.from(new Set(matched.map((booking) => booking.orgPartnerFriendlyName)))
      );
    } else {
      setFilteredBookings(bookings);
      setVenueFilters(
        Array.from(new Set(bookings.map((booking) => booking.orgPartnerFriendlyName)))
      );
    }
  }, [bookings]);

  // Sync venue filters and activity types as pages accumulate
  useEffect(() => {
    const seen = new Set();
    setActivityTypes(
      bookings
        .filter((b) => b.baseActivityId && !seen.has(b.baseActivityId) && seen.add(b.baseActivityId))
        .map((b) => ({ baseActivityId: b.baseActivityId, baseActivityName: b.baseActivityName, baseActivityIcon: b.baseActivityIcon }))
    );
    setVenueFilters(Array.from(new Set(bookings.map((b) => b.orgPartnerFriendlyName))));
  }, [bookings]);

  useEffect(() => {
    setTitle("Bookings");
    setBackButton(true);
    setFilterFunc(toggleFilterOverlay);
  }, [filterOverlayVisible]);

  // Initial load / reset when user changes
  useEffect(() => {
    if (!user?.id) return;
    pageRef.current = 0;
    setBookings([]);
    setHasMore(true);
    fetchPage(0);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver — fires when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          pageRef.current += 1;
          fetchPage(pageRef.current);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, fetchPage]);

  useEffect(() => {
    filterBookings();
  }, [filterBookings]);

  const toggleFilterOverlay = () => {
    setFilterOverlayVisible((prevState) => !prevState);
  };

  const handleActivityClick = (activity) => {
    setSelectedActivity((prevSelected) => {
      if (prevSelected.includes(activity)) {
        return prevSelected.filter((item) => item !== activity);
      } else {
        return [...prevSelected, activity];
      }
    });
  };

  const handleVenueClick = (venue) => {
    setSelectedVenue((prevSelected) => {
      if (prevSelected.includes(venue)) {
        return prevSelected.filter((item) => item !== venue);
      } else {
        return [...prevSelected, venue];
      }
    });
  };

  return (
    <div className="relative bg-sportsLightGrey p-[24px] pb-28 min-h-screen">
      <p className="font-semibold tracking-widest text-[#101820] text-[12px]">
        Upcoming
      </p>
      <p className="font-semibold text-[24px]">Bookings</p>
      <div className="relative w-full mb-[30px] mt-[30px]">
        <TextInput
          name="search"
          type="text"
          label="Search"
          className="w-full [&>.relative>input]:pr-[42px] [&>.label]:p-0 [&>.label]:pb-1 bg-white"
          autoComplete="off"
          onChange={(e) => {
            searchBookings(e.target.value);
          }}
          value={searchTerm}
        />
        <SearchIcon className="absolute w-6 h-6 stroke-black right-[14px] bottom-[14px]" />
      </div>
      {loading && (
        <div className="flex justify-center items-center py-16">
          <div className="w-8 h-8 border-4 border-sportsYellow border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && filteredBookings.length === 0 && (
        <div className="flex flex-col gap-y-8 overflow-y-auto">
          <>
            <p className="text-[24px] leading-[34px] font-semibold text-[#101820]">
              Sorry we couldn&apos;t find any matches for: {searchTerm}
            </p>
            <p className="font-semibold text-[#909090]">
              Please try searching with another term
            </p>
          </>
        </div>
      )}
      {!loading && filteredBookings.length !== 0 && (
        <div className="flex flex-row gap-x-3 mb-[30px] pt-[50px] border-t border-dashed border-sportsLightGrey no-scrollbar mx-[-24px] px-[24px] overflow-x-scroll">
          <div
            className={`
              flex p-2 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-white
              ${selectedVenue.length === 0 ? "!border-sportsYellow" : ""}
            `}
          >
            <p
              className="text-[10px] flex w-[40px] justify-center"
              onClick={() => {
                setSelectedVenue([]);
                setSearchTerm("");
                setSelectedActivity([]);
                setDateFrom("");
                setDateTo("");
                setVenueFilters(
                  Array.from(
                    new Set(
                      bookings.map((booking) => booking.orgPartnerFriendlyName)
                    )
                  )
                );
              }}
            >
              All
            </p>
          </div>
          {venueFilters.map((venue, index) => (
            <div
              className={`flex p-2 rounded-full font-medium cursor-pointer border-[2px] border-transparent w-fit bg-white 
                ${selectedVenue.includes(venue) ? "!border-sportsYellow" : ""}`}
              onClick={() => {
                handleVenueClick(venue);
              }}
              key={index}
            >
              <p className="text-[10px] flex text-nowrap">{venue?.replace(/\b\w/g, (c) => c.toUpperCase())}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-y-8">
        {filteredBookings.map((booking, index) => (
          <BookingCard key={index} booking={booking} />
        ))}
      </div>
      <div ref={sentinelRef} className="h-1" />
      {loadingMore && (
        <div className="flex justify-center items-center py-8">
          <div className="w-6 h-6 border-4 border-sportsYellow border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {filterOverlayVisible && (
        <div className="absolute top-0 left-0 h-screen bg-white p-6 z-[999] max-w-[430px] w-full">
          <div className="flex flex-row justify-between items-center">
            <p className="font-semibold text-[24px]">Filters</p>
            <button onClick={toggleFilterOverlay}>
              <img src="/images/icons/close.svg" />
            </button>
          </div>
          <div className="flex flex-col gap-x-4">
            <DatePicker label="Date From" name="date-from" onChange={(e) => setDateFrom(e.target.value)} />
            <DatePicker label="Date To" name="date-to" onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {activityTypes.length > 0 && (
            <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-4 mt-4">
              <p className="text-sportsBlack font-medium text-base leading-[26px] mb-3">Activity</p>
              <div className="flex flex-row gap-3 flex-wrap">
                {activityTypes.map((a) => (
                  <div
                    key={a.baseActivityId}
                    className={`flex items-center gap-1 p-2 px-3 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-sportsLightGrey ${
                      selectedActivity.includes(a.baseActivityId) ? "!border-sportsYellow" : ""
                    }`}
                    onClick={() => handleActivityClick(a.baseActivityId)}
                  >
                    {a.baseActivityIcon && <img src={a.baseActivityIcon} alt={a.baseActivityName} className="w-4 h-4" />}
                    <p className="text-[10px] capitalize">{a.baseActivityName}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Button
              variant="primary"
              className="w-full mt-4"
              onClick={() => {
                setFilterOverlayVisible(false);
                filterBookings();
              }}
            >
              Update Search
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
